import { useEffect, useRef, useState } from "react";
import { useLiveState } from "@/lib/useLiveState";
import { mediaUrl } from "@/lib/api";
import { computeParts, formatClock } from "@/lib/timer";
import { matchStart } from "@/lib/display";
import DisplayScoreboard, { ScoreboardFrame } from "@/components/display/DisplayScoreboard";
import DisplayTable from "@/components/display/DisplayTable";
import DisplayTicker from "@/components/display/DisplayTicker";

const ADS_PER_BREAK = 5;
const TABLE_MS = 30000;
const RETURN_MS = 30000;

function useScale() {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const compute = () => setScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);
  return scale;
}

function useTick(ms = 500) {
  const [, set] = useState(0);
  useEffect(() => {
    const i = setInterval(() => set((x) => x + 1), ms);
    return () => clearInterval(i);
  }, [ms]);
}

// A field's scoreboard is visible when its game is in progress, or briefly after
// halftime/end, or when manually forced. Otherwise the display shows ads.
function isVisible(live, now, timeless) {
  if (!live) return false;
  if (timeless) return live.manual === "show";
  if (live.manual === "show") return true;
  if (live.manual === "hide") return false;
  const ph = live.timer?.phase;
  const st = live.status;
  if (st === "ended" || ph === "ended" || ph === "halftime") {
    if (!live.phase_since) return false;
    return now - new Date(live.phase_since).getTime() < RETURN_MS;
  }
  return st === "live";
}

// Deterministic, clock-driven ad/table rotation so that every display instance
// (multiple windows, /display, /display1 …) shows the exact same content at the
// same moment — like watching one shared stream. The current item is a pure
// function of wall-clock time, not per-client timers.
function buildPlaylist(snapshot) {
  const activeAds = (snapshot?.media || []).filter((m) => m.active && (m.slot || "fullscreen") === "fullscreen");
  const demo = snapshot?.settings?.demo_mode === true;
  const groups = snapshot?.groups || [];
  const groupStandings = snapshot?.group_standings || {};
  const standingsRows = snapshot?.standings?.rows || [];
  const matchdays = snapshot?.matchdays || [];
  const matches = snapshot?.matches || [];
  const noMdCount = matches.filter((m) => !m.matchday_id).length;

  const standingsFilled = standingsRows.some((r) => (r.sp || 0) > 0);
  const hasGroupsResults = groups.some((g) => ((groupStandings[g.id]?.rows) || []).some((r) => (r.sp || 0) > 0));
  const showStandings = demo || standingsFilled;
  const showGroups = demo ? groups.length > 0 : hasGroupsResults;

  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const anyDates = matchdays.some((md) => md.date);
  let scheduleSteps;
  if (matchdays.length === 0) {
    scheduleSteps = [{ view: "schedule", matchdayId: null }];
  } else {
    const mdList = demo || !anyDates ? matchdays : matchdays.filter((md) => md.date === today);
    scheduleSteps = [...mdList.map((md) => ({ view: "schedule", matchdayId: md.id })), ...(demo && noMdCount > 0 ? [{ view: "schedule", matchdayId: "none" }] : [])];
  }

  const tableViews = [
    ...(showStandings ? [{ view: "standings" }] : []),
    ...(showGroups ? [{ view: "groups" }] : []),
    ...scheduleSteps,
  ].map((v) => ({ ...v, dur: TABLE_MS }));

  const playlist = [];
  if (activeAds.length === 0) {
    playlist.push(...tableViews);
  } else {
    for (let i = 0; i < activeAds.length; i += ADS_PER_BREAK) {
      activeAds.slice(i, i + ADS_PER_BREAK).forEach((ad) => {
        const base = ad.type === "video" ? 15 : 10;
        playlist.push({ view: "ad", ad, dur: (ad.duration || base) * 1000 });
      });
      playlist.push(...tableViews);
    }
  }
  return playlist.length ? playlist : [{ view: "standings", dur: TABLE_MS }];
}

function currentStep(playlist, now) {
  const total = playlist.reduce((s, it) => s + it.dur, 0);
  if (total <= 0) return { item: playlist[0], offset: 0 };
  let pos = now % total;
  for (const it of playlist) {
    if (pos < it.dur) return { item: it, offset: pos };
    pos -= it.dur;
  }
  return { item: playlist[playlist.length - 1], offset: 0 };
}

// The played position of a video is synced only for late-joining displays
// (large offset). At a normal clip start the offset is ~0, so we DON'T seek —
// seeking (even to ~0.3s) forces a full download on non-faststart MP4s, causing
// a long black screen, and a tiny start offset makes the clip loop-restart right
// before the rotation switches. Playing straight from 0 avoids both.
function AdView({ item, offset = 0 }) {
  const ref = useRef(null);
  const offRef = useRef(offset);
  offRef.current = offset;
  useEffect(() => {
    const v = ref.current;
    if (!v || item?.type !== "video") return undefined;
    const sync = () => {
      const off = (offRef.current || 0) / 1000;
      if (off < 1.5) return; // fresh clip start -> play from 0, no seek
      let target = off;
      if (v.duration && Number.isFinite(v.duration) && v.duration > 0) target = target % v.duration;
      try { v.currentTime = target; } catch (e) { /* not seekable yet */ }
    };
    if (v.readyState >= 1) sync();
    else v.addEventListener("loadedmetadata", sync, { once: true });
    return () => v.removeEventListener("loadedmetadata", sync);
  }, [item?.id, item?.type]);
  if (!item) return <div className="w-full h-full bg-black" />;
  return (
    <div className="w-full h-full bg-black" data-testid="display-ads">
      {item.type === "video" ? (
        <video ref={ref} key={item.id} src={mediaUrl(item.id)} autoPlay muted loop playsInline className="w-full h-full object-contain" />
      ) : item.type === "pdf" ? (
        <iframe key={item.id} src={`${mediaUrl(item.id)}#toolbar=0&navpanes=0`} title="pdf" className="w-full h-full bg-white" />
      ) : (
        <img key={item.id} src={mediaUrl(item.id)} alt="" className="w-full h-full object-contain fade-in" />
      )}
    </div>
  );
}

function MiniScoreboard({ live, showTimer, large }) {
  const parts = computeParts(live.timer);
  const phase = live.timer?.phase || "playing";
  const ended = live.status === "ended" || phase === "ended";
  const color = live.field_color || "#00F0FF";
  const status = ended ? <span className="text-[#E5FF00] font-display">ENDE</span> : phase === "halftime" ? <span className="text-[#00F0FF] font-display">HALBZEIT</span> : showTimer === false ? <span className="text-[#8A99AD] font-display">{(live.timer?.half || 1)}. Halbzeit</span> : formatClock(parts.main);
  const injury = showTimer !== false && !ended && phase === "playing" && parts.injury > 0;

  if (large) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-[#050609] border-2 border-[#1E2638] p-12 gap-6 overflow-hidden" data-testid="mini-scoreboard">
        {live.field_name && (
          <div className="font-display font-black uppercase tracking-wider text-6xl px-8 py-2 rounded-lg" style={{ background: color, color: "#0A0C10" }}>{live.field_name}</div>
        )}
        <div className="flex items-center gap-10 justify-center">
          <span className="font-mono-num font-extrabold text-[13rem] leading-none text-[#0088FF]">{live.score1}</span>
          <span className="font-mono-num font-extrabold text-9xl leading-none text-[#2b3550]">:</span>
          <span className="font-mono-num font-extrabold text-[13rem] leading-none text-[#FF6600]">{live.score2}</span>
        </div>
        <div className="flex items-center gap-5 justify-center w-full px-4">
          <span className="font-display font-black uppercase text-6xl truncate flex-1 text-right text-[#0088FF]">{live.team1_name}</span>
          <span className="font-display font-black text-5xl text-[#2b3550] shrink-0">:</span>
          <span className="font-display font-black uppercase text-6xl truncate flex-1 text-[#FF6600]">{live.team2_name}</span>
        </div>
        <div className="font-mono-num font-bold text-9xl text-[#E5FF00]">
          {status}
          {injury && <span className="text-[#00F0FF] text-6xl ml-3">+{formatClock(parts.injury)}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-[#050609] border-2 border-[#1E2638] p-8 overflow-hidden" data-testid="mini-scoreboard">
      {live.field_name && (
        <div className="font-display font-black uppercase tracking-wider text-4xl mb-6 px-6 py-1.5 rounded-lg" style={{ background: color, color: "#0A0C10" }}>{live.field_name}</div>
      )}
      <div className="flex items-center gap-6 w-full justify-center">
        <span className="font-display font-black uppercase text-5xl truncate flex-1 text-right">{live.team1_name}</span>
        <span className="font-mono-num font-extrabold text-8xl text-[#0088FF]">{live.score1}</span>
        <span className="font-mono-num font-extrabold text-6xl text-[#2b3550]">:</span>
        <span className="font-mono-num font-extrabold text-8xl text-[#FF6600]">{live.score2}</span>
        <span className="font-display font-black uppercase text-5xl truncate flex-1">{live.team2_name}</span>
      </div>
      <div className="mt-6 font-mono-num font-bold text-7xl text-[#E5FF00]">
        {status}
        {injury && <span className="text-[#00F0FF] text-4xl ml-3">+{formatClock(parts.injury)}</span>}
      </div>
    </div>
  );
}

function KickoffView({ snapshot, matches, fieldColors }) {
  return (
    <ScoreboardFrame snapshot={snapshot}>
      <div className="w-full h-full flex flex-col items-center justify-center gap-10 p-16" data-testid="display-kickoff">
        <div className="flex items-center gap-4">
          <span className="w-5 h-5 rounded-full bg-[#FF2A55] live-dot" />
          <span className="font-display font-black uppercase tracking-[0.2em] text-6xl text-[#E5FF00]">Gleich geht&#39;s los</span>
        </div>
        {matches.map((m) => (
          <div key={m.id} className="flex items-center gap-8 w-full justify-center">
            <span className="font-mono-num font-bold text-6xl text-[#00F0FF] w-44 text-right">{m.start_time}</span>
            {m.field && <span className="font-display font-black uppercase text-4xl rounded-lg px-5 py-2 shrink-0" style={{ background: fieldColors[m.field] || "#00F0FF", color: "#0A0C10" }}>{m.field}</span>}
            <span className="font-display font-black uppercase text-5xl flex-1 text-right truncate">{m.team1_name}</span>
            <span className="font-mono-num text-4xl text-[#2b3550] shrink-0">vs</span>
            <span className="font-display font-black uppercase text-5xl flex-1 truncate">{m.team2_name}</span>
          </div>
        ))}
      </div>
    </ScoreboardFrame>
  );
}

export default function Display({ displayKey = "main" }) {
  const { snapshot } = useLiveState();
  const scale = useScale();
  useTick(500);

  if (!snapshot) {
    return (
      <div className="display-root">
        <div className="text-[#8A99AD] font-display uppercase tracking-widest text-2xl">Verbinde mit Steuerung…</div>
      </div>
    );
  }

  const fields = snapshot.fields || [];
  const lives = snapshot.lives || [];
  const cfg = snapshot.settings?.displays || {};
  let selected = cfg[displayKey];
  if (displayKey === "main" && selected === undefined) selected = fields.map((f) => f.id);
  selected = selected || [];
  const now = Date.now();
  const timeless = snapshot.settings?.show_timer === false;

  let visibleLives = selected
    .map((fid) => lives.find((l) => l.field_id === fid))
    .filter((l) => l && isVisible(l, now, timeless));
  if (fields.length === 0 && displayKey === "main" && lives[0] && isVisible(lives[0], now, timeless)) {
    visibleLives = [lives[0]];
  }
  const n = Math.min(visibleLives.length, 4);

  // Kickoff preview: shortly before a match on one of this display's fields.
  const fieldColors = Object.fromEntries(fields.map((f) => [f.name, f.color || "#00F0FF"]));
  const fieldNames = selected.map((fid) => (fields.find((f) => f.id === fid) || {}).name).filter(Boolean);
  const lead = snapshot.settings?.kickoff_lead ?? 10;
  const nowMs = Date.now();
  const nowDate = new Date();
  const liveMatchIds = new Set(lives.filter((l) => l.status === "live").map((l) => l.match_id).filter(Boolean));
  const kickoff = lead > 0 && fieldNames.length > 0
    ? (snapshot.matches || []).filter((m) => {
        if (!m.field || !fieldNames.includes(m.field) || m.status === "Beendet" || liveMatchIds.has(m.id) || !m.start_time) return false;
        const diff = (matchStart(m, snapshot.matchdays, nowDate) - nowMs) / 60000;
        return diff >= 0 && diff <= lead;
      }).sort((a, b) => matchStart(a, snapshot.matchdays, nowDate) - matchStart(b, snapshot.matchdays, nowDate))
    : [];

  let content;
  let statusLabel;
  if (n === 0 && kickoff.length > 0) {
    content = <KickoffView snapshot={snapshot} matches={kickoff.slice(0, 4)} fieldColors={fieldColors} />;
    statusLabel = "Anstoß";
  } else if (n === 0) {
    const playlist = buildPlaylist(snapshot);
    const { item: adStep, offset: adOffset } = currentStep(playlist, now);
    statusLabel = adStep.view === "ad" ? "Werbung" : "Tabelle";
    content = (
      <>
        {adStep.view === "ad" && <AdView item={adStep.ad} offset={adOffset} />}
        {adStep.view === "standings" && <DisplayTable snapshot={snapshot} variant="standings" elapsed={adOffset} />}
        {adStep.view === "groups" && <DisplayTable snapshot={snapshot} variant="groups" elapsed={adOffset} />}
        {adStep.view === "schedule" && <DisplayTable snapshot={snapshot} variant="schedule" matchdayId={adStep.matchdayId} elapsed={adOffset} />}
      </>
    );
  } else if (n === 1) {
    content = <DisplayScoreboard snapshot={snapshot} live={visibleLives[0]} />;
  } else if (n === 2) {
    content = (
      <ScoreboardFrame snapshot={snapshot}>
        <div className="w-full h-full grid grid-cols-2 gap-1" data-testid="display-split2">
          <MiniScoreboard live={visibleLives[0]} showTimer={!timeless} large={snapshot.settings?.display_mode === "large"} />
          <MiniScoreboard live={visibleLives[1]} showTimer={!timeless} large={snapshot.settings?.display_mode === "large"} />
        </div>
      </ScoreboardFrame>
    );
  } else {
    content = (
      <ScoreboardFrame snapshot={snapshot}>
        <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-1" data-testid="display-quad">
          {visibleLives.slice(0, 4).map((l) => <MiniScoreboard key={l.field_id || "s"} live={l} showTimer={!timeless} />)}
        </div>
      </ScoreboardFrame>
    );
  }

  return (
    <div className="display-root" data-testid="display-root">
      <div className="display-stage" style={{ transform: `scale(${scale})` }} data-testid={`display-${displayKey}`}>
        <div className="w-full h-full flex flex-col relative">
          {statusLabel === "Werbung" && (
            <div className="absolute top-3 left-3 z-40 flex items-center gap-2 bg-black/55 backdrop-blur px-3 py-1.5 rounded-full pointer-events-none" data-testid="display-status">
              <span className="w-2.5 h-2.5 rounded-full bg-[#00F0FF] live-dot" />
              <span className="font-display font-bold uppercase tracking-wider text-lg text-white">Live: Werbung</span>
            </div>
          )}
          <div className="flex-1 overflow-hidden relative">{content}</div>
          <DisplayTicker snapshot={snapshot} />
        </div>
      </div>
    </div>
  );
}
